import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import { parseServerDate } from '../utils/dates.js';
import { fetchFeatures } from '../utils/features.js';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(str) {
  if (!str) return '—';
  return parseServerDate(str).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function SeasonStats({ season }) {
  const pct = season.process_count > 0
    ? Math.round((season.process_completed / season.process_count) * 100)
    : 0;
  return (
    <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin-top:10px">
      {[
        { val: season.process_count ?? 0,     label: 'Processen' },
        { val: season.process_completed ?? 0, label: 'Afgerond' },
        { val: season.task_count ?? 0,        label: 'Taken' },
      ].map(s => (
        <div key={s.label} style="text-align:center;background:rgba(255,255,255,0.1);border-radius:8px;padding:8px 4px">
          <div style="font-size:18px;font-weight:700">{s.val}</div>
          <div style="font-size:10px;opacity:0.75">{s.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── New Season Wizard ─────────────────────────────────────────────────────────

const GOAL_ACTIONS = { doorzetten: 'Doorzetten', bereikt: 'Bereikt', stoppen: 'Stoppen' };

// Visie & doelen: the open goals, when this user sees the feature and may edit goals
function useOpenGoals(enabled) {
  const auth = useAuth();
  const [goals, setGoals] = useState([]);
  useEffect(() => {
    if (!enabled) return;
    fetchFeatures(`${auth.userId}:${auth.activeClubId}`)
      .then(f => (f.goals ? api('/api/goals') : null))
      .then(d => {
        if (!d?.can_edit) return;
        setGoals(d.focus_areas.flatMap(a => a.goals.filter(g => !g.stopped_at && g.status !== 'bereikt').map(g => ({ ...g, focus_area: a.title }))));
      })
      .catch(() => {});
  }, [enabled, auth.userId, auth.activeClubId]);
  return goals;
}

function NewSeasonWizard({ hasActive, onDone, onCancel }) {
  const [step, setStep] = useState(1);
  // Goals are closed together with the current season, so only when there is one
  const openGoals = useOpenGoals(hasActive);
  const [goalActions, setGoalActions] = useState({}); // goal id → bereikt | stoppen (default doorzetten)
  const goalDecisions = Object.entries(goalActions).filter(([, a]) => a !== 'doorzetten').map(([goalId, action]) => ({ goal_id: Number(goalId), action }));
  const [form, setForm] = useState({
    new_name: '',
    new_start_date: '',
    new_end_date: '',
    cancel_open_processes: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleFinish() {
    setSaving(true);
    setError(null);
    try {
      const result = await api('/api/seasons/new-season-wizard', {
        method: 'POST',
        body: JSON.stringify({ ...form, goal_decisions: goalDecisions }),
      });
      if (result.goals_failed) alert('Het nieuwe seizoen is gestart, maar de keuzes voor de doelen zijn niet opgeslagen. Pas die op de pagina Visie & doelen aan.');
      onDone(result);
    } catch (err) {
      setError(err.code === 'invalid_goal_decisions'
        ? 'De doelen zijn intussen gewijzigd. Sluit de wizard en open hem opnieuw.'
        : 'Er ging iets mis. Probeer het opnieuw.');
    }
    setSaving(false);
  }

  return (
    <div style="display:flex;flex-direction:column;gap:16px">
      {/* Step indicator */}
      <div style="display:flex;gap:0;margin-bottom:4px">
        {[1, 2, 3].map(n => (
          <div key={n} style="flex:1;text-align:center">
            <div style={`width:28px;height:28px;border-radius:50%;margin:0 auto 4px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;background:${step >= n ? 'var(--color-primary)' : 'var(--color-border)'};color:${step >= n ? '#fff' : 'var(--color-text-muted)'}`}>
              {n}
            </div>
            <div style="font-size:10px;color:var(--color-text-muted)">{['Nieuw seizoen','Oud seizoen','Bevestigen'][n-1]}</div>
          </div>
        ))}
      </div>

      {/* Step 1 — New season details */}
      {step === 1 && (
        <div style="display:flex;flex-direction:column;gap:10px">
          <div>
            <label class="form-label">Naam nieuw seizoen *</label>
            <input class="form-input" placeholder="bijv. Seizoen 2026-2027"
              value={form.new_name} onInput={e => set('new_name', e.target.value)} />
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
            <div>
              <label class="form-label">Startdatum</label>
              <input class="form-input" type="date" value={form.new_start_date} onInput={e => set('new_start_date', e.target.value)} />
            </div>
            <div>
              <label class="form-label">Einddatum</label>
              <input class="form-input" type="date" value={form.new_end_date} onInput={e => set('new_end_date', e.target.value)} />
            </div>
          </div>
          <div style="display:flex;gap:6px;justify-content:flex-end;margin-top:4px">
            <Button variant="secondary" onClick={onCancel}>Annuleren</Button>
            <Button variant="primary" onClick={() => setStep(2)} disabled={!form.new_name.trim()}>Volgende →</Button>
          </div>
        </div>
      )}

      {/* Step 2 — Handle old season */}
      {step === 2 && (
        <div style="display:flex;flex-direction:column;gap:12px">
          {hasActive ? (
            <>
              <div style="padding:12px;background:var(--color-bg-secondary);border-radius:8px;border:1px solid var(--color-border);font-size:13px">
                <strong>Het huidige seizoen wordt gearchiveerd.</strong> Wat moet er gebeuren met open processen?
              </div>
              <label style="display:flex;align-items:flex-start;gap:10px;cursor:pointer;font-size:13px">
                <input type="checkbox" style="margin-top:2px;flex-shrink:0"
                  checked={form.cancel_open_processes}
                  onChange={e => set('cancel_open_processes', e.target.checked)} />
                <div>
                  <div style="font-weight:500">Open processen annuleren</div>
                  <div style="color:var(--color-text-muted);font-size:12px;margin-top:2px">
                    Alle processen die nog niet zijn afgerond worden geannuleerd. Laat dit uitgevinkt om ze open te laten staan in het archief.
                  </div>
                </div>
              </label>
              {openGoals.length > 0 && (
                <div style="display:flex;flex-direction:column;gap:6px">
                  <div style="font-size:13px;font-weight:500">Doelen — wat gebeurt ermee?</div>
                  {openGoals.map(g => (
                    <div key={g.id} style="display:flex;align-items:center;gap:8px;font-size:13px;min-width:0">
                      <div style="flex:1;min-width:0">
                        <div style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{g.title}</div>
                        <div style="font-size:11px;color:var(--color-text-muted)">{g.focus_area}</div>
                      </div>
                      <select class="form-input" style="width:auto;height:34px;padding:0 8px;font-size:12px" aria-label={`Doel ${g.title}`}
                        value={goalActions[g.id] || 'doorzetten'} onChange={e => setGoalActions(a => ({ ...a, [g.id]: e.target.value }))}>
                        {Object.entries(GOAL_ACTIONS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              )}
              <div style="padding:10px 12px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;font-size:12px;color:#1e40af">
                💡 Leden, rollen en secties worden automatisch meegenomen naar het nieuwe seizoen — die hoef je niet opnieuw in te stellen.
              </div>
            </>
          ) : (
            <div style="padding:12px;background:var(--color-bg-secondary);border-radius:8px;font-size:13px;color:var(--color-text-muted)">
              Er is geen actief seizoen om te archiveren. Er wordt direct een nieuw seizoen aangemaakt.
            </div>
          )}
          <div style="display:flex;gap:6px;justify-content:flex-end;margin-top:4px">
            <Button variant="secondary" onClick={() => setStep(1)}>← Terug</Button>
            <Button variant="primary" onClick={() => setStep(3)}>Volgende →</Button>
          </div>
        </div>
      )}

      {/* Step 3 — Confirm */}
      {step === 3 && (
        <div style="display:flex;flex-direction:column;gap:12px">
          <div style="padding:14px;background:var(--color-bg-secondary);border-radius:10px;border:1px solid var(--color-border);font-size:13px;display:flex;flex-direction:column;gap:6px">
            <div><strong>Nieuw seizoen:</strong> {form.new_name}</div>
            {form.new_start_date && <div><strong>Periode:</strong> {formatDate(form.new_start_date)} – {form.new_end_date ? formatDate(form.new_end_date) : '…'}</div>}
            {hasActive && <div><strong>Huidig seizoen:</strong> wordt gearchiveerd {form.cancel_open_processes ? '+ open processen geannuleerd' : '(open processen blijven open)'}</div>}
            <div><strong>Leden & rollen:</strong> worden overgenomen ✓</div>
            {openGoals.length > 0 && (
              <div><strong>Doelen:</strong> {goalDecisions.length === 0 ? 'alle doelen lopen door' : [
                ['bereikt', 'bereikt'], ['stoppen', 'gestopt'],
              ].map(([k, l]) => { const n = goalDecisions.filter(d => d.action === k).length; return n ? `${n} ${l}` : null; }).filter(Boolean).join(', ') + ', de rest loopt door'}</div>
            )}
          </div>
          {error && <div class="alert alert-error" style="font-size:13px">{error}</div>}
          <div style="display:flex;gap:6px;justify-content:flex-end;margin-top:4px">
            <Button variant="secondary" onClick={() => setStep(2)}>← Terug</Button>
            <Button variant="primary" onClick={handleFinish} disabled={saving}>
              {saving ? 'Bezig…' : '🚀 Nieuw seizoen starten'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Archive Confirm Modal ─────────────────────────────────────────────────────

function ArchiveModal({ season, onConfirm, onCancel }) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  async function handleConfirm() {
    setSaving(true);
    await onConfirm(cancelOpen);
    setSaving(false);
  }

  return (
    <div style="display:flex;flex-direction:column;gap:12px">
      <div style="font-size:13px">
        Je staat op het punt <strong>"{season.name}"</strong> te archiveren. Dit kan niet ongedaan worden gemaakt.
      </div>
      <label style="display:flex;align-items:flex-start;gap:10px;cursor:pointer;font-size:13px">
        <input type="checkbox" style="margin-top:2px;flex-shrink:0"
          checked={cancelOpen} onChange={e => setCancelOpen(e.target.checked)} />
        <div>
          <div style="font-weight:500">Open processen annuleren</div>
          <div style="color:var(--color-text-muted);font-size:12px;margin-top:2px">Alle lopende processen in dit seizoen worden geannuleerd.</div>
        </div>
      </label>
      <div style="display:flex;gap:6px;justify-content:flex-end;margin-top:4px">
        <Button variant="secondary" onClick={onCancel}>Annuleren</Button>
        <Button variant="danger" onClick={handleConfirm} disabled={saving}>
          {saving ? 'Bezig…' : 'Archiveren'}
        </Button>
      </div>
    </div>
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────

export default function SeasonsView() {
  const auth = useAuth();
  const canManage = auth.hasPermission('settings', 'update');

  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [editSeason, setEditSeason] = useState(null);
  const [archiveSeason, setArchiveSeason] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    api('/api/seasons')
      .then(data => setSeasons(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const activeSeason = seasons.find(s => s.is_active);
  const archived = seasons.filter(s => s.is_archived);
  const inactive = seasons.filter(s => !s.is_active && !s.is_archived);

  async function handleWizardDone(newSeason) {
    setShowWizard(false);
    const updated = await api('/api/seasons').catch(() => []);
    setSeasons(Array.isArray(updated) ? updated : []);
  }

  async function handleArchive(season, cancelOpen) {
    await api(`/api/seasons/${season.id}/archive`, {
      method: 'POST',
      body: JSON.stringify({ cancel_open_processes: cancelOpen }),
    });
    setArchiveSeason(null);
    const updated = await api('/api/seasons').catch(() => []);
    setSeasons(Array.isArray(updated) ? updated : []);
  }

  async function handleActivate(season) {
    await api(`/api/seasons/${season.id}/activate`, { method: 'POST' });
    const updated = await api('/api/seasons').catch(() => []);
    setSeasons(Array.isArray(updated) ? updated : []);
  }

  async function handleSaveEdit() {
    setSavingEdit(true);
    try {
      await api(`/api/seasons/${editSeason.id}`, { method: 'PUT', body: JSON.stringify(editForm) });
      setSeasons(s => s.map(x => x.id === editSeason.id ? { ...x, ...editForm } : x));
      setEditSeason(null);
    } catch {}
    setSavingEdit(false);
  }

  if (loading) return (
    <div>
      <div class="page-header"><h1 class="page-title">Seizoenbeheer</h1></div>
      <div class="text-muted text-center" style="padding:60px">Laden…</div>
    </div>
  );

  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">📅 Seizoenbeheer</h1>
        {canManage && !showWizard && (
          <Button variant="primary" size="sm" onClick={() => setShowWizard(true)}>
            🚀 Nieuw seizoen starten
          </Button>
        )}
      </div>

      {/* Wizard */}
      {showWizard && (
        <div style="margin-bottom:24px">
          <Card title="Nieuw seizoen starten">
            <NewSeasonWizard
              hasActive={!!activeSeason}
              onDone={handleWizardDone}
              onCancel={() => setShowWizard(false)}
            />
          </Card>
        </div>
      )}

      {/* Active season */}
      {activeSeason ? (
        <div style="margin-bottom:20px">
          <div style="border-radius:14px;background:linear-gradient(135deg,var(--color-primary),#6366f1);padding:20px 24px;color:#fff;position:relative;overflow:hidden">
            <div style="position:absolute;top:-20px;right:-20px;font-size:80px;opacity:0.1">🏆</div>
            <div style="font-size:11px;opacity:0.75;text-transform:uppercase;letter-spacing:.06em;margin-bottom:4px">Actief seizoen</div>
            <div style="font-size:22px;font-weight:800;margin-bottom:4px">{activeSeason.name}</div>
            <div style="font-size:13px;opacity:0.8">
              {activeSeason.start_date ? formatDate(activeSeason.start_date) : 'Geen startdatum'}
              {activeSeason.end_date && (
                <>
                  {' → '}{formatDate(activeSeason.end_date)}
                  {daysUntil(activeSeason.end_date) !== null && daysUntil(activeSeason.end_date) > 0 && (
                    <span style="margin-left:8px;background:rgba(255,255,255,0.2);padding:1px 8px;border-radius:10px;font-size:11px">
                      nog {daysUntil(activeSeason.end_date)} dagen
                    </span>
                  )}
                </>
              )}
            </div>
            <SeasonStats season={activeSeason} />
            {canManage && (
              <div style="margin-top:12px;display:flex;gap:6px">
                <button
                  onClick={() => { setEditSeason(activeSeason); setEditForm({ name: activeSeason.name, start_date: activeSeason.start_date || '', end_date: activeSeason.end_date || '', notes: activeSeason.notes || '' }); }}
                  style="background:rgba(255,255,255,0.2);border:none;color:#fff;padding:5px 12px;border-radius:8px;cursor:pointer;font-size:12px"
                >
                  ✏️ Bewerken
                </button>
                <button
                  onClick={() => setArchiveSeason(activeSeason)}
                  style="background:rgba(255,255,255,0.15);border:1px solid rgba(255,255,255,0.3);color:#fff;padding:5px 12px;border-radius:8px;cursor:pointer;font-size:12px"
                >
                  📦 Archiveren
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        !showWizard && (
          <div style="margin-bottom:20px;padding:20px;border-radius:12px;border:2px dashed var(--color-border);text-align:center;color:var(--color-text-muted)">
            <div style="font-size:32px;margin-bottom:8px">📅</div>
            <div style="font-weight:500;margin-bottom:4px">Geen actief seizoen</div>
            {canManage && <div style="font-size:13px">Klik op "Nieuw seizoen starten" om een seizoen aan te maken.</div>}
          </div>
        )
      )}

      {/* Inactive (not yet archived) */}
      {inactive.length > 0 && (
        <Card title="⏸ Inactieve seizoenen" style="margin-bottom:16px">
          <div style="display:flex;flex-direction:column;gap:8px">
            {inactive.map(s => (
              <SeasonRow key={s.id} season={s} canManage={canManage}
                onActivate={() => handleActivate(s)}
                onEdit={() => { setEditSeason(s); setEditForm({ name: s.name, start_date: s.start_date || '', end_date: s.end_date || '', notes: s.notes || '' }); }}
                onArchive={() => setArchiveSeason(s)} />
            ))}
          </div>
        </Card>
      )}

      {/* Archived */}
      {archived.length > 0 && (
        <Card title={`📦 Archief (${archived.length})`}>
          <div style="display:flex;flex-direction:column;gap:8px">
            {archived.map(s => (
              <SeasonRow key={s.id} season={s} canManage={canManage} archived
                onActivate={() => handleActivate(s)}
                onEdit={() => { setEditSeason(s); setEditForm({ name: s.name, start_date: s.start_date || '', end_date: s.end_date || '', notes: s.notes || '' }); }} />
            ))}
          </div>
        </Card>
      )}

      {seasons.length === 0 && !showWizard && (
        <Card>
          <div class="text-center text-muted" style="padding:40px">
            <div style="font-size:32px;margin-bottom:12px">📅</div>
            <div style="font-size:15px;font-weight:500;margin-bottom:6px">Nog geen seizoenen</div>
            <div style="font-size:13px">Maak je eerste seizoen aan om processen te koppelen aan een seizoensperiode.</div>
          </div>
        </Card>
      )}

      {/* Edit modal */}
      <Modal isOpen={!!editSeason} title="Seizoen bewerken" onClose={() => setEditSeason(null)}>
        <div style="display:flex;flex-direction:column;gap:10px">
          <div>
            <label class="form-label">Naam</label>
            <input class="form-input" value={editForm.name || ''} onInput={e => setEditForm(f => ({...f, name: e.target.value}))} />
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
            <div>
              <label class="form-label">Startdatum</label>
              <input class="form-input" type="date" value={editForm.start_date || ''} onInput={e => setEditForm(f => ({...f, start_date: e.target.value}))} />
            </div>
            <div>
              <label class="form-label">Einddatum</label>
              <input class="form-input" type="date" value={editForm.end_date || ''} onInput={e => setEditForm(f => ({...f, end_date: e.target.value}))} />
            </div>
          </div>
          <div>
            <label class="form-label">Notities</label>
            <textarea class="form-textarea" rows={2} value={editForm.notes || ''} onInput={e => setEditForm(f => ({...f, notes: e.target.value}))} />
          </div>
          <div style="display:flex;gap:6px;justify-content:flex-end;margin-top:4px">
            <Button variant="secondary" onClick={() => setEditSeason(null)}>Annuleren</Button>
            <Button variant="primary" onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit ? '…' : 'Opslaan'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Archive confirm modal */}
      <Modal isOpen={!!archiveSeason} title="Seizoen archiveren" onClose={() => setArchiveSeason(null)}>
        {archiveSeason && (
          <ArchiveModal
            season={archiveSeason}
            onConfirm={(cancelOpen) => handleArchive(archiveSeason, cancelOpen)}
            onCancel={() => setArchiveSeason(null)}
          />
        )}
      </Modal>
    </div>
  );
}

function SeasonRow({ season, canManage, archived, onActivate, onEdit, onArchive }) {
  return (
    <div style={`display:flex;align-items:flex-start;gap:12px;padding:10px 12px;border-radius:8px;border:1px solid var(--color-border);background:var(--color-bg-secondary);opacity:${archived ? 0.75 : 1}`}>
      <div style="flex:1;min-width:0">
        <div style="font-size:13px;font-weight:600">{season.name}</div>
        <div style="font-size:11px;color:var(--color-text-muted);margin-top:2px">
          {season.start_date ? `${formatDate(season.start_date)}${season.end_date ? ' → ' + formatDate(season.end_date) : ''}` : 'Geen periode'}
          {archived && season.archived_at && ` · Gearchiveerd op ${formatDate(season.archived_at)}`}
        </div>
        <div style="display:flex;gap:8px;font-size:11px;color:var(--color-text-muted);margin-top:4px">
          <span>⚙️ {season.process_count ?? 0} processen</span>
          <span>✅ {season.process_completed ?? 0} afgerond</span>
          <span>📋 {season.task_count ?? 0} taken</span>
        </div>
      </div>
      {canManage && (
        <div style="display:flex;gap:4px;flex-shrink:0">
          {!archived && (
            <button onClick={onActivate} title="Activeren"
              style="background:none;border:1px solid var(--color-border);border-radius:6px;padding:3px 8px;cursor:pointer;font-size:11px;color:var(--color-primary);font-weight:500">
              Activeren
            </button>
          )}
          {archived && (
            <button onClick={onActivate} title="Heractiveren"
              style="background:none;border:1px solid var(--color-border);border-radius:6px;padding:3px 8px;cursor:pointer;font-size:11px;color:var(--color-text-muted)">
              Heractiveren
            </button>
          )}
          <button onClick={onEdit} style="background:none;border:1px solid var(--color-border);border-radius:6px;padding:3px 8px;cursor:pointer;font-size:12px">✏️</button>
          {!archived && onArchive && (
            <button onClick={onArchive} style="background:none;border:1px solid var(--color-border);border-radius:6px;padding:3px 8px;cursor:pointer;font-size:12px">📦</button>
          )}
        </div>
      )}
    </div>
  );
}
